import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MaterialContext } from "../context/MaterialContext";
import { UserContext } from "../context/UserContext";
import { threeBarsIcon, trashIcon } from "../components/Icons";
import LoadingDots from "../components/LoadingDots";
import { API_BASE_URL } from "../utilities/Static";
import {
  formatBrandId,
  formatBrandMaterialName,
  getBrandDataForSlug,
} from "../utilities/brandData";
import { getSessionCustomerBrand } from "../utilities/loginAccount";
import { isVendorUser } from "../utilities/AuthState";
import { isDesignerHiddenStatus } from "../utilities/MaterialDetails";
import { getCollectionPdfTheme } from "../utilities/pdfTheme";

const PDF_ENDPOINT = `${API_BASE_URL}/pdf`;
const DEFAULT_COLLECTION_TITLE = "my collection";
const PDF_SESSION_HISTORY_KEY = "collection-pdf-session-history";

const getInitialSessionPdfHistory = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const storedHistory = window.sessionStorage.getItem(
      PDF_SESSION_HISTORY_KEY,
    );

    if (!storedHistory) {
      return [];
    }

    const parsedHistory = JSON.parse(storedHistory);
    return Array.isArray(parsedHistory) ? parsedHistory : [];
  } catch (error) {
    console.error("Unable to load PDF session history:", error);
    return [];
  }
};

const getBrandInfo = (nikeData, slug, brand) =>
  getBrandDataForSlug(nikeData, slug, brand).map((material) => {
    const idLabel = formatBrandId(material);
    return idLabel
      ? `${formatBrandMaterialName(material)} || ${idLabel}`
      : formatBrandMaterialName(material);
  });

const buildPdfMaterial = (
  item,
  nikeData,
  includePricing = false,
  brand,
) => ({
  title: item?.title || "",
  description: item?.description || "",
  vendor: item?.vendor || "",
  brand: getBrandInfo(nikeData, item?.slug || "", brand),
  slug: item?.slug || "",
  price: item?.price || "",
  incPrice: includePricing,
  image: item?.image || item?.images?.[0] || "",
});

function CollectionPdf() {
  const navigate = useNavigate();
  const { nikeData, allMaterials, ROOT_SERVER } = useContext(MaterialContext);
  const { collection, updateCollection, userInformation } =
    useContext(UserContext);
  const sessionBrand = getSessionCustomerBrand(userInformation);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [incPricing, setIncPricing] = useState(false);
  const [swatchViewMode, setSwatchViewMode] = useState("grid");
  const [selectedImageByMaterial, setSelectedImageByMaterial] = useState({});
  const [sessionPdfHistory, setSessionPdfHistory] = useState(
    getInitialSessionPdfHistory,
  );
  const [collectionTitle, setCollectionTitle] = useState(
    DEFAULT_COLLECTION_TITLE,
  );
  const [collectionDescription, setCollectionDescription] = useState("");
  const [manualPdfLink, setManualPdfLink] = useState("");
  const pendingPdfWindowRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.sessionStorage.setItem(
      PDF_SESSION_HISTORY_KEY,
      JSON.stringify(sessionPdfHistory),
    );
  }, [sessionPdfHistory]);

  const materialById = useMemo(
    () => new Map(allMaterials.map((material) => [material.id, material])),
    [allMaterials],
  );

  const selectedMaterials = useMemo(() => {
    if (!Array.isArray(collection) || collection.length === 0) {
      return [];
    }

    return collection
      .map((id) => materialById.get(id))
      .filter((material) => Boolean(material))
      .filter(
        (material) =>
          isVendorUser(userInformation) ||
          !isDesignerHiddenStatus(material.status),
      );
  }, [collection, materialById, userInformation]);

  const selectedCount = selectedMaterials.length;
  const canExport = selectedCount > 0;

  useEffect(() => {
    setSelectedImageByMaterial((previousMap) => {
      const nextMap = {};

      selectedMaterials.forEach((item) => {
        const imageCount = Array.isArray(item.images) ? item.images.length : 0;

        if (imageCount === 0) {
          return;
        }

        const previousIndex = previousMap[item.id];
        nextMap[item.id] =
          typeof previousIndex === "number" &&
          previousIndex >= 0 &&
          previousIndex < imageCount
            ? previousIndex
            : 0;
      });

      return nextMap;
    });
  }, [selectedMaterials]);

  const getSelectedImageName = (item) => {
    const materialImages = Array.isArray(item?.images) ? item.images : [];

    if (materialImages.length === 0) {
      return "";
    }

    const requestedIndex = selectedImageByMaterial[item.id] ?? 0;
    const safeIndex =
      requestedIndex >= 0 && requestedIndex < materialImages.length
        ? requestedIndex
        : 0;

    return materialImages[safeIndex] || materialImages[0] || "";
  };

  const getSwatchImageSrc = (item, imageName = getSelectedImageName(item)) => {
    const selectedImageName = imageName || "";

    if (!selectedImageName) {
      return "";
    }

    return `${ROOT_SERVER}${item.vendor}/${item.slug}/swatch/${selectedImageName}`;
  };

  const handleSelectSwatchImage = (materialId, imageIndex) => {
    setSelectedImageByMaterial((previousMap) => ({
      ...previousMap,
      [materialId]: imageIndex,
    }));
  };

  const openInNewTab = (url) => {
    const nextWindow = window.open(url, "_blank");

    if (!nextWindow) {
      setManualPdfLink(url || "");
      setPdfError(
        "Popup blocked. Please allow popups for this site to open the PDF in a new tab, or use the manual link below.",
      );
      return;
    }

    // Preserve security intent while avoiding false "blocked" reports.
    nextWindow.opener = null;
    setManualPdfLink("");
    setPdfError("");
  };

  const handleReorderMaterials = (result) => {
    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    const nextCollection = selectedMaterials.map((item) => item.id);
    const [movedId] = nextCollection.splice(result.source.index, 1);
    nextCollection.splice(result.destination.index, 0, movedId);
    updateCollection(nextCollection);
  };

  const handleRemoveMaterial = (id) => {
    updateCollection(collection.filter((materialId) => materialId !== id));
  };

  const appendSessionPdfRecord = (pdfLink) => {
    if (!pdfLink) {
      return;
    }

    const sessionRecord = {
      id: `pdf-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      url: pdfLink,
      title: collectionTitle.trim() || DEFAULT_COLLECTION_TITLE,
      description: collectionDescription.trim(),
      itemCount: selectedCount,
      createdAt: new Date().toISOString(),
    };

    setSessionPdfHistory((previousHistory) =>
      [sessionRecord, ...previousHistory].slice(0, 12),
    );
  };

  const handleClearSessionHistory = () => {
    setSessionPdfHistory([]);
  };

  const handleDownload = async () => {
    if (!canExport) {
      return;
    }

    const pendingWindow = window.open("about:blank", "_blank");
    pendingPdfWindowRef.current = pendingWindow;

    setPdfLoading(true);
    setPdfError("");
    setManualPdfLink("");

    try {
      const material = selectedMaterials.map((item) =>
        buildPdfMaterial(
          {
            ...item,
            image: getSwatchImageSrc(item),
          },
          nikeData,
          incPricing,
          sessionBrand,
        ),
      );

      const res = await axios.post(PDF_ENDPOINT, {
        material,
        pageTitle: collectionTitle.trim() || DEFAULT_COLLECTION_TITLE,
        collectionDescription: collectionDescription.trim(),
        theme: getCollectionPdfTheme(),
      });

      if (res.status === 200) {
        const pdfLink =
          typeof res.data === "string" ? res.data : res.data?.url || "";

        if (pdfLink) {
          appendSessionPdfRecord(pdfLink);

          if (pendingPdfWindowRef.current) {
            pendingPdfWindowRef.current.location.href = pdfLink;
            pendingPdfWindowRef.current.opener = null;
            pendingPdfWindowRef.current = null;
          } else {
            openInNewTab(pdfLink);
          }
        }

        setCollectionTitle(DEFAULT_COLLECTION_TITLE);
        setCollectionDescription("");
      }
    } catch (error) {
      if (pendingPdfWindowRef.current) {
        pendingPdfWindowRef.current.close();
        pendingPdfWindowRef.current = null;
      }
      console.error("PDF generation error:", error);
      setPdfError("PDF generation failed. Please try again.");
    } finally {
      if (pendingPdfWindowRef.current) {
        pendingPdfWindowRef.current.close();
        pendingPdfWindowRef.current = null;
      }
      setPdfLoading(false);
    }
  };

  const collectionPreview = selectedMaterials.map((item) => ({
    id: item.id,
    title: item.title,
    vendor: item.vendor,
    description: item.description,
    images: Array.isArray(item.images) ? item.images : [],
    selectedImageIndex: selectedImageByMaterial[item.id] ?? 0,
    image: getSwatchImageSrc(item),
  }));

  return (
    <div className="content collection-pdf-page">
      <div className="collection-pdf-topbar">
        <div className="collection-pdf-topbar-copy">
          <p className="collection-pdf-kicker">PDF export</p>
          <h2>Collection PDF</h2>
        </div>
        <button
          type="button"
          className="button-highlight collection-pdf-back-button"
          onClick={() => navigate("/collection")}
        >
          Back to collection
        </button>
      </div>

      {!canExport ? (
        <div className="collection-pdf-empty-state">
          <div className="collection-pdf-empty-card">
            <p className="collection-pdf-kicker">Nothing selected</p>
            <h3>Your collection is empty.</h3>
            <p>
              Add swatches to your collection first, then come back here to
              build the PDF.
            </p>
            <button
              type="button"
              className="button-highlight"
              onClick={() => navigate("/collection")}
            >
              Go to collection
            </button>
          </div>
        </div>
      ) : (
        <div className="collection-pdf-layout">
          <aside className="collection-pdf-summary">
            <div className="collection-pdf-view-toggle-row">
              <div className="collection-pdf-view-meta">
                <p className="collection-pdf-kicker">Selection</p>
                <h3>{selectedCount} swatches ready</h3>
              </div>
              <div className="collection-pdf-view-toggle">
                <button
                  type="button"
                  className={swatchViewMode === "grid" ? "active" : ""}
                  onClick={() => setSwatchViewMode("grid")}
                >
                  Array view
                </button>
                <button
                  type="button"
                  className={swatchViewMode === "list" ? "active" : ""}
                  onClick={() => setSwatchViewMode("list")}
                >
                  List edit view
                </button>
              </div>
            </div>

            {swatchViewMode === "grid" ? (
              <div className="collection-pdf-preview-grid">
                {collectionPreview.map((item) => (
                  <article
                    key={item.id}
                    className="collection-pdf-preview-card"
                  >
                    <div className="collection-pdf-preview-image">
                      {item.image ? (
                        <img src={item.image} alt={item.title} />
                      ) : null}
                      {item.images.length > 1 ? (
                        <div className="collection-pdf-image-selector">
                          {item.images.map((image, imageIndex) => (
                            <button
                              key={`${item.id}-${image}`}
                              type="button"
                              className={`selector-dot ${item.selectedImageIndex === imageIndex ? "active" : ""}`}
                              onClick={() =>
                                handleSelectSwatchImage(item.id, imageIndex)
                              }
                              aria-label={`Select image ${imageIndex + 1} for ${item.title}`}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="collection-pdf-preview-copy">
                      <p>{item.vendor}</p>
                      <h4>{item.title}</h4>
                      <span>{item.description}</span>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <DragDropContext onDragEnd={handleReorderMaterials}>
                <Droppable droppableId="collection-pdf-list">
                  {(provided) => (
                    <div
                      className="collection-pdf-list"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {collectionPreview.map((item, index) => (
                        <Draggable
                          key={String(item.id)}
                          draggableId={String(item.id)}
                          index={index}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <div
                              className={`collection-pdf-list-item ${dragSnapshot.isDragging ? "dragging" : ""}`}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                            >
                              <div
                                className="collection-pdf-list-handle"
                                {...dragProvided.dragHandleProps}
                                aria-label="Reorder swatch"
                              >
                                {threeBarsIcon}
                              </div>
                              <div className="collection-pdf-list-thumb">
                                {item.image ? (
                                  <img src={item.image} alt={item.title} />
                                ) : null}
                              </div>
                              <div className="collection-pdf-list-copy">
                                <p>{item.vendor}</p>
                                <h4>{item.title}</h4>
                                <span>{item.description}</span>
                              </div>
                              <button
                                type="button"
                                className="collection-pdf-list-remove"
                                onClick={() => handleRemoveMaterial(item.id)}
                                aria-label={`Remove ${item.title}`}
                              >
                                {trashIcon}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </aside>

          <section className="collection-pdf-panel">
            <div className="pdf-download">
              <div className="pdf-download-section">
                <div className="pdf-download-header">
                  <div className="pdf-download-heading">
                    <p className="pdf-download-kicker">Settings</p>
                    <h4>Export options</h4>
                  </div>
                  <div className="pdf-download-badge">
                    {selectedCount} items
                  </div>
                </div>

                <div className="pdf-download-body">
                  <div className="pdf-download-copy">
                    <h5>Choose a title and add notes to your PDF.</h5>
                  </div>

                  <label className="pdf-input-group">
                    <span>Collection title</span>
                    <input
                      type="text"
                      value={collectionTitle}
                      placeholder="My collection"
                      onChange={(e) => setCollectionTitle(e.target.value)}
                    />
                  </label>

                  <label className="pdf-input-group">
                    <span>Collection description</span>
                    <input
                      type="text"
                      value={collectionDescription}
                      placeholder="Optional note for the PDF"
                      onChange={(e) => setCollectionDescription(e.target.value)}
                    />
                  </label>

                  {pdfError ? (
                    <div className="pdf-download-error">
                      <p>{pdfError}</p>
                      {manualPdfLink ? (
                        <a
                          href={manualPdfLink}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="pdf-download-manual-link"
                        >
                          Open PDF manually
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="pdf-download-footer">
                  <div key="price" className="checkbox pdf-download-checkbox">
                    <input
                      id="price"
                      type="checkbox"
                      name="price"
                      checked={incPricing}
                      onChange={() => setIncPricing(!incPricing)}
                    />
                    <label htmlFor="price">Include pricing</label>
                  </div>

                  <div className="pdf-download-actions">
                    <button
                      type="button"
                      className="pdf-download-button primary"
                      onClick={handleDownload}
                      disabled={pdfLoading}
                    >
                      {pdfLoading ? (
                        <>
                          <LoadingDots dotsClassName="pdfDownloadLoadingDots" />
                          <h4>Preparing</h4>
                        </>
                      ) : (
                        <h4>Generate PDF</h4>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {sessionPdfHistory.length > 0 ? (
              <div className="collection-pdf-history-block">
                <div className="pdf-session-history">
                  <div className="pdf-session-history-header">
                    <div className="pdf-session-history-copy">
                      <p className="pdf-session-history-kicker">Session</p>
                      <h5>Previously generated PDFs</h5>
                    </div>
                    <button
                      type="button"
                      className="pdf-session-history-clear"
                      onClick={handleClearSessionHistory}
                    >
                      Clear
                    </button>
                  </div>

                  <div className="pdf-session-history-list">
                    {sessionPdfHistory.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        className="pdf-session-history-item"
                        onClick={() => openInNewTab(entry.url)}
                      >
                        <div>
                          <strong>{entry.title}</strong>
                          <span>
                            {entry.itemCount} items
                            {entry.description ? ` • ${entry.description}` : ""}
                          </span>
                        </div>
                        <span>
                          {new Date(entry.createdAt).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}

export default CollectionPdf;
